import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authActions, changeFieldValue, setLoginSuccess, startLogin } from 'tenantUser/redux/authActions';
import { AppState } from '../../../redux/rootReducer';
import { coreActions } from '../../../redux/actions/coreActions';
import { NotificationAlert } from '../../../classes/NotificationAlert';
import { LoginGQLDocument } from 'tenantUser/api/gql/login';
import TokenService from '../../../config/gql/TokenService';
import { clientNoAuth } from '../../../config/gql/clientNoAuth';
import { ApolloError } from '@apollo/client';

export const useLoginFormData: LoginFormDataHook = () => {
    const dispatch = useDispatch();

    const [termsChecked, setTermsChecked] = useState(false);

    const handleTerms = () => {
        setTermsChecked((prev) => !prev);
    };

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            dispatch(changeFieldValue(event));
        },
        [dispatch],
    );

    const email = useSelector(({ tempAuth }: AppState) => tempAuth.email);
    const password = useSelector(({ tempAuth }: AppState) => tempAuth.password);

    const handleLogin = async () => {
        dispatch(startLogin());

        try {
            const variables = { input: { email: email?.value, password: password?.value } };
            const response = await clientNoAuth.mutate({ mutation: LoginGQLDocument, variables });

            if (response.data?.LogIn) {
                const loginData = response.data?.LogIn;
                dispatch(setLoginSuccess(loginData));

                if (loginData.token && loginData.refresh_token) {
                    const tokenService = TokenService.getInstance();
                    tokenService.saveToken(loginData.token, loginData.refresh_token);
                }

                return;
            }
        } catch (error) {
            if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
                const { path } = error.graphQLErrors[0];

                if (path && path[0] === 'LogIn') {
                    dispatch(
                        coreActions.notification.set({
                            message: 'notices.wrongCredentials',
                            level: NotificationAlert.ERROR,
                        }),
                    );
                } else {
                    dispatch(
                        coreActions.notification.set({
                            message: 'notices.criticalError',
                            level: NotificationAlert.ERROR,
                        }),
                    );
                }
            } else {
                dispatch(
                    coreActions.notification.set({ message: 'notices.criticalError', level: NotificationAlert.ERROR }),
                );
            }
        } finally {
            dispatch(authActions.clearLoadingState());
        }
    };

    const handleKeyEnterPressed = ({ nativeEvent }: KeyboardEvent<HTMLInputElement>) => {
        if (!!nativeEvent && 'key' in nativeEvent && 'Enter' === nativeEvent.key) handleLogin();
    };

    return {
        handleChange,
        handleLogin,
        handleTerms,
        termsChecked,
        handleKeyEnterPressed,
    };
};
export default useLoginFormData;

type LoginFormDataHook = () => LoginFormDataReturn;
type LoginFormDataReturn = {
    handleChange: HandleEvent;
    termsChecked: boolean;
    handleTerms: () => void;
    handleLogin: () => void;
    handleKeyEnterPressed: (event: KeyboardEvent<HTMLInputElement>) => void;
};
type HandleEvent = (event: ChangeEvent<HTMLInputElement>) => void;
